<script setup lang="ts">
import type { ClippedImagePath } from '~/components/crystallize/ClippedImage.vue'
import type { ContentBlockData } from '~/utils/crystallize/pieces'

const props = withDefaults(
  defineProps<{
    dir?: Direction
    frameShape: 'hexagon' | 'octagon' | 'decagon' | 'circle'
    data: ContentBlockData
  }>(),
  {
    dir: 'ltr',
  },
)

const path = computed<ClippedImagePath>(() => `${props.frameShape}-${props.dir === 'ltr' ? 'right' : 'left'}`)
</script>

<template>
  <ContentGrid class="items-center" :class="{ 'grid-flow-dense': dir === 'rtl' }">
    <CrystallizeClippedImage
      v-if="path && data.image?.firstImage"
      data-image
      :image="data.image.firstImage"
      :path="path"
      class="hidden lg:block md:col-span-2 lg:col-span-4 2xl:col-span-5 overflow-x-hidden"
      img-class="h-[360px] object-cover w-full"
      :class="{
        'rounded-l-9xl [--translate-x:-80%] 2xl:col-start-2': dir === 'ltr',
        'rounded-r-9xl [--translate-x:80%] order-last': dir === 'rtl',
      }"
    />
    <div class="col-span-4 2xl:col-span-6 flex flex-col gap-l lg:px-2xl">
      <h2 v-text="data.title" />
      <CrystallizeRichText v-for="(block, key) in data.text" :key="key" :block="block" class="font-heading text-2xl font-light" />
    </div>
  </ContentGrid>
</template>

<style scoped>
/* Make the image slide in if the browser supports it and the user is ok with motion */
@supports (animation-timeline: view()) {
  @media (prefers-reduced-motion: no-preference) {
    [data-image] {
      animation: slide-in 1 both;
      animation-timeline: view(block);
      animation-range: entry 25% cover 80%;
    }
  }
}

@keyframes slide-in {
  from {
    transform: translateX(var(--translate-x));
  }
  to {
    transform: translateX(0);
  }
}
</style>
